import React, { useEffect, useState } from 'react';
import { Download, MoreHorizontal, Play } from 'lucide-react';
import { prettyNumber } from '../../CallDialog';
import { formatTime } from '../../ui';
import { callDay, formatDuration } from '../../calls/callModel';
import { RECORDING_STATUS } from '../recordingsModel';

function RowMenu({ rec, at, can, onAction, onClose }) {
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [onClose]);
  const items = [['view', 'View details'], ['call', 'Open the call'], ['copy', 'Copy number'],
    can.delete && ['delete', 'Delete recording', 'danger']].filter(Boolean);
  return (
    <div className="cl-menu" style={{ top: at.y, left: at.x }} role="menu" onClick={(e) => e.stopPropagation()}>
      {items.map(([k, label, tone]) => <button key={k} type="button" role="menuitem" className={tone || ''} onClick={() => { onClose(); onAction(k, rec); }}>{label}</button>)}
    </div>
  );
}

/** The recordings: contact, call type, duration, date and time, team member and actions; a row opens it on the right. */
export default function RecordingsTable({ items, offset, activeId, selected, onSelect, onView, onAction, can }) {
  const [menu, setMenu] = useState(null);
  const allChecked = items.length > 0 && items.every((r) => selected.has(r.id));
  return (
    <div className="table-scroll">
      <table className="full-page-table cl-table rc-table">
        <thead><tr>
          <th><input type="checkbox" aria-label="Select all shown" checked={allChecked} onChange={() => onSelect(items.map((r) => r.id), !allChecked)} /></th>
          <th>#</th><th>Contact / Number</th><th>Call Type</th><th>Duration</th><th>Date &amp; Time</th><th>Agent</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {items.map((r, i) => {
            const [statusLabel, tone] = RECORDING_STATUS[r.status] || [r.status, 'grey'];
            return (
              <tr key={r.id} className={r.id === activeId ? 'active' : ''} onClick={() => onView(r)} tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onView(r); }} aria-selected={r.id === activeId}>
                <td onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Select recording ${r.id}`} checked={selected.has(r.id)} onChange={() => onSelect([r.id], !selected.has(r.id))} /></td>
                <td className="cell-muted">{offset + i + 1}</td>
                <td><b>{r.contact_name || prettyNumber(r.number)}</b><small className="cell-muted">{r.contact_name ? prettyNumber(r.number) : 'Not in contacts'}</small></td>
                <td>
                  <span className={`rc-type ${r.direction}`}>{r.kind === 'voicemail' ? 'Voicemail' : r.direction === 'inbound' ? 'Incoming' : 'Outgoing'}</span>
                  {r.status !== 'available' && <span className={`rc-status ${tone}`}>{statusLabel}</span>}
                </td>
                <td>{formatDuration(r.duration_seconds)}</td>
                <td><b className="rc-date">{callDay(r.started_at)}</b><small className="cell-muted">{formatTime(r.started_at)}</small></td>
                <td>{r.agent?.name || <span className="cell-muted">—</span>}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="rc-actions">
                    {can.play && <button type="button" className="rc-icon" aria-label={`Play recording ${r.id}`} disabled={!r.playable} title={r.playable ? 'Play' : `Not playable (${statusLabel})`} onClick={() => onAction('play', r)}><Play size={14} /></button>}
                    {can.download && <button type="button" className="rc-icon" aria-label={`Download recording ${r.id}`} disabled={!r.playable} title="Download" onClick={() => onAction('download', r)}><Download size={14} /></button>}
                    <button type="button" className="rc-icon" aria-label={`More actions for recording ${r.id}`}
                      onClick={(e) => { const b = e.currentTarget.getBoundingClientRect(); setMenu({ rec: r, at: { x: Math.max(8, b.right - 190), y: Math.min(b.bottom + 4, window.innerHeight - 180) } }); }}>
                      <MoreHorizontal size={14} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {menu && <RowMenu rec={menu.rec} at={menu.at} can={can} onAction={onAction} onClose={() => setMenu(null)} />}
    </div>
  );
}
